import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Work as WorkIcon,
  Storage as StorageIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';

const Dokumentacija: React.FC = () => {
  const [expandedMainSection, setExpandedMainSection] = useState<string | false>(false);
  const [expandedSubSection, setExpandedSubSection] = useState<string | false>(false);

  const workScheduleSections = [
    {
      id: 'basic-usage',
      title: 'Darbo Grafiko Naudojimo Pagrindai',
      icon: <WorkIcon />,
      content: `# Darbo Grafiko Naudojimo Pagrindai

## Kas yra Darbo Grafikas?

Darbo Grafikas yra sistema, skirta darbuotojų darbo laiko valdymui ir koordinavimui Mokslo Salos tinkle.

## Pagrindiniai Funkcionalumai

### 1. Savo Grafiko Peržiūra
- Eikite į "Darbo grafikas" skyrių
- Pasirinkite "Mano grafikas" tabą
- Peržiūrėkite savo suplanuotą darbo laiką

### 2. Skyrių Vaizdas (Administratoriams)
- Eikite į "Darbo grafikas" → "Skyrių vaizdas" tabą
- Peržiūrėkite visų darbuotojų grafikus vienoje vietoje
- Filtruokite pagal skyrius
- Redaguokite grafikus tiesiogiai iš lentelės
- Sistema optimizuota greitam veikimui

### 3. Mėnesio Valandų Reikalavimai
- **"Patvirtintos mėnesio valandos"** - rodo, kiek valandų turite išdirbti per mėnesį
- **"Faktinės valandos"** - rodo jūsų išdirbtą laiką
- **"Valandų balansas"** - rodo skirtumą tarp reikalingų ir faktinių valandų

### 4. Valstybinės Šventės
- Sistema automatiškai skaičiuoja valstybinėse šventėse dirbtas valandas
- Naujiems darbuotojams automatiškai pridedamos visos valstybinės šventės
- "Valstybinės šventės patvirtinimas" leidžia patvirtinti šventinius dienas

### 5. Statusų Spalvos
- **Dirba** - kremiškai oranžinė spalva
- **Atostogos** - kremiškai žalia spalva
- **Mamadienis/Tėvadienis** - kremiškai mėlyna spalva
- **Dirba Nuotoliu** - kremiškai geltona spalva
- **Ligos atostogos** - kremiškai šviesiai žalia spalva
- **Poilsio diena** - kremiškai violetinė spalva
- **Valstybinė šventė** - kremiškai rožinė spalva

### 6. Perspėjimai
- Jei "Dirba" arba "Dirba Nuotoliu" statusui neįvestas pertraukos laikas, bus rodomas didesnis perspėjimo ženklas ⚠️`
    },
    {
      id: 'schedule-changes',
      title: 'Grafiko Pakeitimo Procesas',
      icon: <WorkIcon />,
      content: `# Grafiko Pakeitimo Procesas

## Darbuotojams

### Kaip Pateikti Grafiko Pakeitimo Prašymą

1. **Pridėti Įrašą**
- Eikite į "Darbo grafikas" → "Mano grafikas"
- Spustelėkite "Pridėti įrašą" mygtuką arba tiesiog spustelėkite darbo planavimo lentelėje.

2. **Užpildykite Formą**
- Pasirinkite datą, kurios grafiką norite pakeisti
- Nurodykite pradžios ir pabaigos laiką
- Įveskite pakeitimo priežastį

3. **Pateikite Prašymą**
- Patikrinkite duomenis
- Spustelėkite "Išsaugoti"
- Prašymas bus išsiųstas administratoriui patvirtinti

## Administratoriams

### Prašymų Valdymas

1. **Peržiūrėti Prašymus**
- Eikite į "Darbo grafikas" → "Skyrių vaizdas"
- Pamatysite visus laukiančius prašymus
- Spustelėkite ant prašymo, kad peržiūrėtumėte detales

2. **Patvirtinti arba Atmesti**
- Spustelėkite "Patvirtinti" arba "Atmesti"
- Įveskite komentarą (neprivaloma)
- Sprendimas bus išsiųstas darbuotojui`
    },
    {
      id: 'month-locks',
      title: 'Mėnesių Užšaldymo Sistema',
      icon: <WorkIcon />,
      content: `# Mėnesių Užšaldymo Sistema

## Kas yra Mėnesių Užšaldymas?

Mėnesių užšaldymas leidžia administratoriams uždrausti laisvus grafiko pakeitimus tam tikriems mėnesiams (visiems arba pasirinktiems skyriams).

## Administratoriams

### Kaip Užšaldyti Mėnesį

1. **Eikite į Mėnesių užšaldymas**
- Darbo grafikas → "Mėnesių užšaldymas" tabas

2. **Tvarkykite mėnesį**
- Spustelėkite "Tvarkyti" ant mėnesio kortelės
- Pasirinkite: atšildytas, užšaldyti visus skyrius, arba užšaldyti pasirinktus
- Įveskite priežastį (neprivaloma)

3. **Išsaugokite**
- Užšaldytas mėnuo turi šaltą (mėlyną) rėmelį
- Dalinai užšaldytas rodo "Dalinai užšaldyta" ir skyrių sąrašą

### Kaip Atšildyti Mėnesį

1. **Raskite užšaldytą mėnesį**
- Peržiūrėkite mėnesių korteles
- Raskite mėnesį su "Užšaldyta" arba "Dalinai užšaldyta" statusu

2. **Atšildykite**
- Spustelėkite "Tvarkyti"
- Pasirinkite "Atšildytas"
- Išsaugokite

## Darbuotojams

### Ką Reiškia Užšaldytas Mėnuo?

- Negalite atlikti grafiko pakeitimų be administratoriaus patvirtinimo (jei jūsų skyrius užšaldytas).
- Sistema automatiškai praneš, jei bandysite keisti užšaldytą mėnesį.`
    },
    {
      id: 'state-holidays',
      title: 'Valstybinės Šventės',
      icon: <WorkIcon />,
      content: `# Valstybinės Šventės

## Kaip Veikia Valstybinės Šventės

### Automatinis Skaičiavimas

Sistema automatiškai:
- Atpažįsta valstybinės šventės dienas
- Skaičiuoja dirbtas valandas šventinėmis dienomis
- Prideda jas prie mėnesio valandų balanso
- **Naujiems darbuotojams automatiškai pridedamos visos valstybinės šventės**

### Valstybinės Šventės Patvirtinimas

1. **Peržiūrėti Šventes**
   - Eikite į "Darbo grafikas"
   - Peržiūrėkite "Valstybinės šventės patvirtinimas" skyrių

2. **Patvirtinti Šventes**
   - Spustelėkite ant šventės
   - Patvirtinkite arba atmeskite šventinę dieną
   - Sistema atnaujins valandų skaičiavimą

### Svarbu Žinoti

- **Patvirtintos šventės** įskaičiuojamos į mėnesio valandų balansą
- Administratoriai gali redaguoti šventinius dienas
- Nauji darbuotojai automatiškai gauna visas esamas valstybinės šventes`
    },
    {
      id: 'department-view',
      title: 'Skyrių Vaizdas (Administratoriams)',
      icon: <WorkIcon />,
      content: `# Skyrių Vaizdas

## Kas yra Skyrių Vaizdas?

Skyrių Vaizdas yra administratorių funkcija, leidžianti peržiūrėti ir valdyti visų darbuotojų darbo grafikus vienoje vietoje.

## Pagrindinės Funkcijos

### 1. Visų Darbuotojų Peržiūra
- Peržiūrėkite visų darbuotojų grafikus vienoje lentelėje
- Darbuotojai rodomi eilutėse, datos - stulpeliuose
- Greitai identifikuokite tuščius arba neužpildytus laikotarpius

### 2. Skyrių Filtravimas
- Filtruokite darbuotojus pagal skyrius
- Pasirinkite vieną arba kelis skyrius vienu metu
- Greitai pereikite tarp skirtingų skyrių

### 3. Tiesioginis Redagavimas
- Spustelėkite bet kurį langelį, kad redaguotumėte grafiką
- Greitai keiskite statusus ir laikus
- Patvirtinkite arba atmeskite prašymus tiesiogiai iš lentelės

### 4. Spalvų Legenda
- **Dirba** - kremiškai oranžinė spalva
- **Atostogos** - kremiškai žalia spalva
- **Mamadienis/Tėvadienis** - kremiškai mėlyna spalva
- **Dirba Nuotoliu** - kremiškai geltona spalva
- **Ligos atostogos** - kremiškai šviesiai žalia spalva
- **Poilsio diena** - kremiškai violetinė spalva
- **Valstybinė šventė** - kremiškai rožinė spalva

### 5. Perspėjimai
- Jei "Dirba" arba "Dirba Nuotoliu" statusui neįvestas pertraukos laikas, rodomas didesnis perspėjimo ženklas ⚠️
- Perspėjimai padeda greitai identifikuoti neužpildytus laukus

### 6. Naudojimo Patarimai
- Naudokite skyrių filtrus, kad sumažintumėte duomenų kiekį
- Peržiūrėkite mėnesį po mėnesio, kad veiktų greičiau
- Sistema optimizuota greitam veikimui net su daug darbuotojų`
    }
  ];

  const storageSections = [
    {
      id: 'storage-basic',
      title: 'Sandėliuko Naudojimo Pagrindai',
      icon: <StorageIcon />,
      content: `# Sandėliuko Naudojimo Pagrindai

## Kas yra Sandėliukas?

Sandėliukas yra inventoriaus valdymo sistema, skirta prekių ir reikmenų sekimui bei valdymui Mokslo Salos organizacijoje.

## Pagrindiniai Funkcionalumai

### 1. Prekių Peržiūra
- Eikite į "Sandėliukas" skyrių
- Peržiūrėkite prekes pagal kategorijas
- Matykite esamą kiekį ir vietą
- Patikrinkite minimalų likutį

### 2. Krepšelis
- Pridėkite prekes į krepšelį
- Peržiūrėkite pasirinktas prekes
- Nurodykite naudojimo paskirtį
- Pateikite prašymą administratoriui

### 3. Transakcijų Žurnalas
- Peržiūrėkite visas prekių operacijas
- Filtruokite pagal tipą, datą ar vartotoją
- Sekite prekių judėjimą

### 4. Administratoriaus Funkcijos
- Pridėti naujas prekes
- Redaguoti esamas prekes
- Nustatyti minimalius likučius
- Patvirtinti arba atmesti prašymus`
    },
    {
      id: 'storage-categories',
      title: 'Prekių Kategorijos',
      icon: <StorageIcon />,
      content: `# Prekių Kategorijos

Sandėliukas turi penkias pagrindines prekių kategorijas:

## 1. Ūkinės Prekės
- Verslo ir ūkinės veiklos prekės
- Naudojamos kasdieninėje veikloje
- Pavyzdžiai: valymo priemonės, įrankiai, įranga

## 2. Kanceliarinės Prekės
- Biuro reikmenys ir kanceliarinės prekės
- Naudojamos darbo vietose
- Pavyzdžiai: popierius, rašikliai, klijai, segtukai

## 3. Ekspozicija
- Ekspozicijos ir parodų prekės
- Naudojamos ekspozicijų organizavimui
- Pavyzdžiai: stendai, vitrinos, eksponatai

## 4. Suvenyrai
- Suvenyrinės prekės
- Naudojamos pardavimui ar dovanoms
- Pavyzdžiai: atminimo dovanėlės, knygos, ženkleliai

## 5. Darbo Reikmenys
- Darbo įrankiai ir reikmenys
- Naudojami konkrečioms darbo užduotims
- Pavyzdžiai: įrankiai, apsaugos priemonės, specialūs reikmenys

## Kategorijų Naudojimas
- Kiekviena kategorija turi savo tabą
- Prekės organizuotos pagal kategoriją
- Lengva rasti reikiamas prekes`
    },
    {
      id: 'storage-cart',
      title: 'Krepšelis ir Prekių Išdavimas',
      icon: <StorageIcon />,
      content: `# Krepšelis ir Prekių Išdavimas

## Kaip Naudotis Krepšeliu

### 1. Prekių Pridėjimas
- Peržiūrėkite prekes bet kurioje kategorijoje
- Spustelėkite "Pridėti" mygtuką šalia prekės
- Pasirinkite norimą kiekį
- Prekė bus pridėta į krepšelį

### 2. Krepšelio Valdymas
- Spustelėkite "Krepšelis" mygtuką viršuje
- Peržiūrėkite visas pasirinktas prekes
- Keiskite kiekius arba pašalinkite prekes
- Išvalykite visą krepšelį, jei reikia

### 3. Prašymo Pateikimas
- Užpildykite naudojimo paskirtį
- Patikrinkite pasirinktas prekes
- Spustelėkite "Pateikti prašymą"
- Prašymas bus išsiųstas administratoriui

### 4. Kiekio Apribojimai
- Negalite paimti daugiau nei yra sandėlyje
- Sistema automatiškai tikrina esamą kiekį
- Jei kiekio nepakanka, bus rodomas perspėjimas

## Administratoriaus Patvirtinimas
- Administratorius peržiūri prašymą
- Gali patvirtinti arba atmesti
- Patvirtinus, prekių kiekiai automatiškai atnaujinami
- Jūs gausite pranešimą apie sprendimą`
    },
    {
      id: 'storage-admin',
      title: 'Administratoriaus Funkcijos',
      icon: <StorageIcon />,
      content: `# Administratoriaus Funkcijos

## Prekių Valdymas

### 1. Naujos Prekės Pridėjimas
- Spustelėkite "Pridėti prekę" mygtuką
- Užpildykite formą:
  - **Pavadinimas** - prekės pavadinimas
  - **Aprašymas** - detalus aprašymas
  - **Kategorija** - pasirinkite kategoriją
  - **Kiekis** - pradinis kiekis
  - **Minimalus likutis** - perspėjimo lygis
  - **Matavimo vienetas** - vnt., kg, l ir kt.
  - **Vieta** - sandėlio vieta
  - **Tiekėjas** - prekės tiekėjas
- Spustelėkite "Išsaugoti"

### 2. Prekių Redagavimas
- Raskite prekę sąraše
- Spustelėkite redagavimo mygtuką
- Atnaujinkite reikiamus laukus
- Išsaugokite pakeitimus

### 3. Minimalių Likučių Nustatymas
- Nustatykite minimalų likutį kiekvienai prekei
- Sistema automatiškai perspės, kai likutis pasieks minimumą
- Tai padeda laiku papildyti atsargas

### 4. Prašymų Valdymas
- Peržiūrėkite laukiančius prašymus
- Patikrinkite prekių prieinamumą
- Patvirtinkite arba atmesti prašymą
- Įveskite komentarą (neprivaloma)

## Atsakomybė
- Administratorius atsako už teisingą inventoriaus valdymą
- Reguliariai tikrinkite atsargas
- Sekite prekių judėjimą per transakcijų žurnalą`
    },
    {
      id: 'storage-transactions',
      title: 'Transakcijų Žurnalas',
      icon: <StorageIcon />,
      content: `# Transakcijų Žurnalas

## Kas yra Transakcijų Žurnalas?

Transakcijų žurnalas registruoja visas prekių operacijas sistemoje - pridėjimus, išdavimus ir pašalinimus.

## Transakcijų Tipai

### 1. Pridėjimas (IN)
- Naujos prekės pridėjimas į sandėlį
- Kiekio padidinimas
- Rodo: kas pridėjo, kada, kiek

### 2. Išdavimas (OUT)
- Prekių išdavimas darbuotojams
- Kiekio sumažinimas
- Rodo: kam išduota, kada, kiek, paskirtis

### 3. Pašalinimas (DELETE)
- Prekių pašalinimas iš sistemos
- Naudojama sugadintoms arba prarastoms prekėms
- Rodo: kas pašalino, kada, priežastis

## Žurnalo Naudojimas

### Filtravimas
- Filtruokite pagal transakcijos tipą
- Filtruokite pagal datą
- Filtruokite pagal vartotoją
- Ieškokite pagal prekės pavadinimą

### Informacija
- Kiekviena transakcija rodo:
  - Prekės pavadinimas
  - Transakcijos tipas
  - Kiekis
  - Ankstesnis ir naujas kiekis
  - Vartotojas
  - Data ir laikas
  - Pastabos

## Svarbu
- Visos operacijos yra registruojamos
- Negalima ištrinti transakcijų
- Žurnalas padeda sekti prekių judėjimą`
    },
    {
      id: 'storage-best-practices',
      title: 'Geriausios Praktikos',
      icon: <StorageIcon />,
      content: `# Geriausios Praktikos

## Darbuotojams

### 1. Prašymų Pateikimas
- **Planuokite iš anksto** - pateikite prašymus laiku
- **Būkite konkretūs** - aiškiai nurodykite naudojimo paskirtį
- **Tikrinkite kiekius** - pažiūrėkite, ar yra pakankamai prekių
- **Sekite prašymus** - patikrinkite prašymo būseną

### 2. Prekių Naudojimas
- **Naudokite atsakingai** - rūpinkitės paimtomis prekėmis
- **Grąžinkite laiku** - jei prekės turi būti grąžinamos
- **Praneškite apie problemas** - sugadinus arba praradus prekę, praneškite per 24 valandas
- **Sekite instrukcijas** - laikykitės prekių naudojimo instrukcijų

### 3. Krepšelio Valdymas
- **Tikrinkite prieš pateikimą** - patikrinkite pasirinktas prekes
- **Nurodykite tikslų kiekį** - imkite tik tai, ko tikrai reikia
- **Išvalykite krepšelį** - jei keičiate sprendimą

## Administratoriams

### 1. Inventoriaus Valdymas
- **Reguliariai tikrinkite** - peržiūrėkite atsargas kas savaitę
- **Nustatykite minimumus** - kiekvienai prekei nustatykite minimalų likutį
- **Sekite tendencijas** - analizuokite, kurios prekės greitai baigiasi
- **Laiku papildykite** - užsakykite prekes prieš pasiekus minimumą

### 2. Prašymų Apdorojimas
- **Apdorokite greitai** - patvirtinkite arba atmesti per 24 valandas
- **Tikrinkite prieinamumą** - patikrinkite, ar yra pakankamai prekių
- **Būkite aiškūs** - jei atmetate, paaiškinkite priežastį
- **Sekite istoriją** - peržiūrėkite vartotojo prašymų istoriją

### 3. Duomenų Tikslumas
- **Atnaujinkite kiekius** - reguliariai atnaujinkite prekių kiekius
- **Tiksliai nurodykite vietas** - padėkite lengvai rasti prekes
- **Užpildykite visus laukus** - pateikite visą reikalingą informaciją
- **Tvarkykite kategorijas** - teisingai priskirkite prekes kategorijoms

## Bendri Patarimai
- **Komunikacija** - jei kyla klausimų, kreipkitės į administratorių
- **Atsakingumas** - visi atsako už teisingą prekių naudojimą
- **Dokumentavimas** - visos operacijos yra dokumentuojamos
- **Bendradarbiavimas** - dirbkite kartu, kad sistema veiktų efektyviai`
    }
  ];

  const pardavimaiSections = [
    {
      id: 'pardavimai-basic',
      title: 'Pardavimų Naudojimo Pagrindai',
      icon: <ShoppingCartIcon />,
      content: `# Pardavimų Naudojimo Pagrindai

## Kas yra Pardavimai?

Pardavimai yra sistema, skirta pardavimų registravimui, inventoriaus papildymui ir ataskaitų generavimui Mokslo Salos organizacijoje.

## Pagrindiniai Funkcionalumai

### 1. Pardavimų Registravimas
- Registruokite parduotas prekes
- Automatiškai atnaujinkite inventoriaus kiekius
- Sekite pardavimus pagal mėnesį ir kategoriją

### 2. Pajamavimas
- Papildykite inventorių naujomis prekėmis
- Atnaujinkite esamų prekių kainas
- Sukurkite naujas prekes tiesiogiai iš duomenų

### 3. Ataskaitos
- Generuokite detalias pardavimų ataskaitas
- Analizuokite pelną ir pardavimus
- Kopijuokite duomenis į Excel

## Reikalingi Leidimai

- **pardavimai.view** - leidimas peržiūrėti Pardavimų skyrių
- Sistema automatiškai patikrina jūsų leidimus prieš rodant skyrių`
    },
    {
      id: 'pardavimai-registration',
      title: 'Pardavimų Registravimas',
      icon: <ShoppingCartIcon />,
      content: `# Pardavimų Registravimas

## Kaip Registruoti Pardavimus

### 1. Pasirinkite Kategoriją
- Eikite į "Pardavimai" → "Pardavimų registravimas" tabą
- Pasirinkite prekių kategoriją iš sąrašo:
  - Ūkinės prekės
  - Kanceliarinės prekės
  - Ekspozicija
  - Suvenyrai
  - Darbo reikmenys

### 2. Pasirinkite Mėnesį
- Pasirinkite mėnesį, kuriam registruojate pardavimus
- Naudokite mėnesio ir metų pasirinkimo lauką

### 3. Įklijuokite Pardavimų Duomenis
- Formatas: **Prekės kodas [TAB] kiekis**
- Ta pati prekė gali būti keliose eilutėse — nereikia jų sujungti rankiniu būdu
- Pavyzdys (nesujungtos eilutės):
  \`\`\`
  Kojinės 000577	3
  Kojinės 000577	2
  Kojinės 000577	5
  \`\`\`
- Sistema automatiškai aptinka tabuliacijos simbolius ir tikrina bendrą kiekį

### 4. Apdorokite Duomenis
- Spustelėkite "Apdoroti duomenis" mygtuką
- Sistema suranda prekes Sandėliuke pagal pavadinimą
- Peržiūrėkite apdorotų pardavimų lentelę

## Statusų Indikatoriai

### Paruošta (Žalia spalva)
- Prekė rasta Sandėliuke
- Pakankamas kiekis atsargose
- Galima registruoti pardavimą

### Nerasta (Geltona spalva)
- Prekė nerasta Sandėliuke
- Patikrinkite pavadinimo rašybą
- Gali reikėti sukurti naują prekę

### Nepakanka atsargų (Raudona spalva)
- Prekė rasta, bet nepakanka kiekio
- Patikrinkite esamą kiekį
- Papildykite atsargas prieš registruojant

### 5. Registruokite Pardavimus
- Patikrinkite visus "Paruošta" statuso prekes
- Spustelėkite "Registruoti pardavimus"
- Sistema automatiškai sumažins inventoriaus kiekius
- Gausite patvirtinimą apie sėkmingą registravimą`
    },
    {
      id: 'pardavimai-pajamavimas',
      title: 'Pajamavimas',
      icon: <ShoppingCartIcon />,
      content: `# Pajamavimas

## Kaip Papildyti Inventorių

### 1. Pasirinkite Kategoriją ir Tiekėją
- Eikite į "Pardavimai" → "Pajamavimas" tabą
- Pasirinkite prekių kategoriją
- Pasirinkite tiekėją iš sąrašo arba sukurkite naują

### 2. Įklijuokite Duomenis
- Formatas: **Barkodas [TAB] Prekės pavadinimas [TAB] Papildymo kiekis [TAB] Savikaina [TAB] Kaina**
- Pavyzdys:
  \`\`\`
  9786098309324	Knyga Steve Jobs	10	9.81	12.50
  9786098309218	Mokomosios kortelės Žmogaus kūnas	20	19.62	25.00
  \`\`\`
- Sistema automatiškai aptinka tabuliacijos simbolius

### 3. Apdorokite Duomenis
- Spustelėkite "Apdoroti duomenis" mygtuką
- Sistema bando surasti prekes pagal barkodą arba pavadinimą
- Peržiūrėkite apdorotų duomenų lentelę

## Prekių Atpažinimas

### Pagal Barkodą
- Sistema pirmiausia bando rasti prekę pagal barkodą
- Jei barkodas sutampa, prekė automatiškai suporuojama

### Pagal Pavadinimą
- Jei barkodas nerastas, sistema ieško pagal pavadinimą
- Tikslus arba dalinis pavadinimo sutapimas

## Statusų Indikatoriai

### Paruošta (Žalia spalva)
- Prekė rasta ir paruošta papildymui
- Visi duomenys teisingi
- Galima papildyti

### Nerasta (Geltona spalva)
- Prekė nerasta Sandėliuke
- Reikia sukurti naują prekę
- Spustelėkite "Naujas produktas" arba "Visi nauji produktai"

### Reikia atnaujinti kainas (Oranžinė spalva)
- Prekė rasta, bet kainos skiriasi
- Sistema rodo seną ir naują kainą
- Spustelėkite "Atnaujinti" norint atnaujinti kainas

### Barkodas nesutampa (Oranžinė spalva)
- Prekė rasta pagal pavadinimą, bet barkodas skiriasi
- Patikrinkite barkodo tikslumą
- Galite tęsti arba pataisyti barkodą

### Laukia sukūrimo (Mėlyna spalva)
- Prekė pažymėta sukurti
- Bus sukurta spustelėjus "Papildyti"

### 4. Sukurkite Naujas Prekes
- Prekėms su "Nerasta" statusu spustelėkite "Naujas produktas"
- Arba spustelėkite "Visi nauji produktai" norint pažymėti visas
- Prekės bus sukurtos su nurodytais duomenimis

### 5. Atnaujinkite Kainas
- Prekėms su "Reikia atnaujinti kainas" spustelėkite "Atnaujinti"
- Sistema atnaujins savikainą ir pardavimo kainą
- Atnaujinimas vyksta iš karto

### 6. Papildykite Inventorių
- Patikrinkite visas "Paruošta" statuso prekes
- Spustelėkite "Papildyti" mygtuką
- Sistema sukurs naujas prekes ir papildys esamas
- Gausite patvirtinimą apie apdorotų prekių skaičių`
    },
    {
      id: 'pardavimai-reports',
      title: 'Ataskaitos',
      icon: <ShoppingCartIcon />,
      content: `# Ataskaitos

## Kaip Generuoti Ataskaitas

### 1. Pasirinkite Parametrus
- Eikite į "Pardavimai" → "Ataskaitos" tabą
- Pasirinkite mėnesį ir metus
- Pasirinkite prekių kategoriją
- Pasirinkite tiekėją (sąrašas atsiranda pasirinkus kategoriją)

### 2. Generuokite Ataskaitą
- Spustelėkite "Generuoti ataskaitą" mygtuką
- Sistema apdoroja duomenis ir parodo rezultatus
- Ataskaita rodo visus prekių duomenis pasirinktam mėnesiui

## Ataskaitos Struktūra

### Stulpeliai

- **Barkodas** - prekės barkodas
- **Prekė** - prekės pavadinimas
- **Papildyta** - kiek prekių buvo papildyta per mėnesį
- **Parduota** - kiek prekių buvo parduota per mėnesį
- **Paimta** - kiek prekių buvo paimta (ne parduota)
- **Likutis** - esamas likutis sandėlyje
- **Savikaina** - prekės savikaina (EUR)
- **Kaina** - pardavimo kaina (EUR)
- **Pelnas** - apskaičiuotas pelnas (EUR)

### Iš viso Eilutė

- Ataskaitos pabaigoje rodoma "Iš viso" eilutė
- Sumuojami: Papildyta, Parduota, Paimta, Likutis, Pelnas
- Padeda greitai įvertinti bendrą veiklą

### 3. Kopijuokite į Excel
- Spustelėkite "Kopijuoti į Excel" mygtuką
- Duomenys bus nukopijuoti į iškarpinę
- Atidarykite Excel ir įklijuokite (Ctrl+V)
- Barkodai bus formatuoti kaip tekstas (nebus konvertuojami į mokslinį formatą)

## Ataskaitos Naudojimas

### Analizė
- Peržiūrėkite, kurios prekės gerai parduodamos
- Identifikuokite prekes, kurių reikia papildyti
- Analizuokite pelningumą pagal prekę

### Planavimas
- Naudokite ataskaitas planuojant užsakymus
- Sekite prekių judėjimą per laiką
- Optimizuokite atsargų valdymą`
    },
    {
      id: 'pardavimai-best-practices',
      title: 'Geriausios Praktikos',
      icon: <ShoppingCartIcon />,
      content: `# Geriausios Praktikos

## Duomenų Formatavimas

### 1. Pardavimų Registravimas
- **Tikslūs pavadinimai** - naudokite tikslų prekės pavadinimą kaip jis yra Sandėliuke
- **Tikslūs kiekiai** - įveskite tikslų parduotų prekių kiekį
- **Tabuliacijos simboliai** - naudokite TAB tarp stulpelių, ne tarpus
- **Vienas prekės pavadinimas eilutėje** - kiekviena prekė atskiroje eilutėje

### 2. Pajamavimas
- **Barkodai** - jei turite barkodą, visada jį įtraukite (padeda tiksliau surasti prekę)
- **Tikslūs kainos** - įveskite tikslų savikainą ir pardavimo kainą
- **Tiekėjų konsistencija** - naudokite tuos pačius tiekėjų pavadinimus
- **Kategorijų teisingumas** - pasirinkite teisingą kategoriją

## Reguliari Priežiūra

### 1. Reguliariai Registruokite Pardavimus
- Registruokite pardavimus kiekvieną dieną arba savaitę
- Neleiskite duomenų kauptis
- Tai padeda laiku papildyti atsargas

### 2. Tikrinkite Ataskaitas
- Reguliariai generuokite ataskaitas
- Analizuokite prekių judėjimą
- Identifikuokite problemas anksčiau

### 3. Tvarkykite Duomenis
- Patikrinkite, ar visos prekės turi barkodus
- Atnaujinkite kainas, kai jos keičiasi
- Išvalykite pasenusius duomenis

## Dažniausios Problemos ir Sprendimai

### Prekė Nerasta
- **Problema**: Prekė nerandama registruojant pardavimus
- **Sprendimas**: Patikrinkite pavadinimo rašybą, arba sukurkite naują prekę Pajamavime

### Nepakanka Atsargų
- **Problema**: Bandote registruoti daugiau nei yra sandėlyje
- **Sprendimas**: Pirmiausia papildykite atsargas Pajamavime

### Kainos Nesutampa
- **Problema**: Pajamavime rodoma, kad kainos skiriasi
- **Sprendimas**: Spustelėkite "Atnaujinti" norint atnaujinti kainas

### Barkodas Nesutampa
- **Problema**: Prekė rasta pagal pavadinimą, bet barkodas skiriasi
- **Sprendimas**: Patikrinkite barkodo tikslumą arba atnaujinkite barkodą prekėje

## Efektyvus Naudojimas

### 1. Masinis Apdorojimas
- Naudokite "Visi nauji produktai" mygtuką Pajamavime
- Tai leidžia greitai pažymėti visas naujas prekes
- Vėliau vienu spustelėjimu sukurkite visas

### 2. Duomenų Kopijavimas
- Naudokite Excel arba kitas programas duomenims paruošti
- Nukopijuokite duomenis tiesiogiai į laukus
- Sistema automatiškai aptinka formatą

### 3. Ataskaitų Naudojimas
- Reguliariai kopijuokite ataskaitas į Excel
- Saugokite ataskaitas analizei
- Naudokite ataskaitas planuojant užsakymus

## Svarbu Žinoti

- **Automatinis atnaujinimas** - registruojant pardavimus, inventoriaus kiekiai atnaujinami automatiškai
- **Istorija** - visos operacijos yra registruojamos Transakcijų žurnale
- **Tikslumas** - visada patikrinkite duomenis prieš registruojant
- **Kategorijos** - teisingai priskirkite prekes kategorijoms, kad būtų lengviau rasti`
    }
  ];

  const handleMainSectionChange = (sectionId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedMainSection(isExpanded ? sectionId : false);
    setExpandedSubSection(false); // Reset sub-sections when main section closes
  };

  const handleSubSectionChange = (sectionId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSubSection(isExpanded ? sectionId : false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dokumentacija
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Išsamūs vadovai ir instrukcijos sistemos modulių naudojimui
        </Typography>
      </Box>

      {/* Welcome Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WorkIcon sx={{ fontSize: 40, mr: 2 }} />
            <Box>
              <Typography variant="h5" component="h2">
                Dokumentacija
              </Typography>
              <Typography variant="body1">
                Dokumentacija suskirstyta pagal puslapio struktūrą.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>


      {/* Main Accordion: Darbo Grafikas */}
      <Accordion
        expanded={expandedMainSection === 'work-schedule'}
        onChange={handleMainSectionChange('work-schedule')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <WorkIcon />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5">Darbo Grafikas</Typography>
              <Typography variant="body2" color="text.secondary">
                Spustelėkite, kad išplėstumėte ir sužinotumėte daugiau
              </Typography>
            </Box>
      </Box>
        </AccordionSummary>
        <AccordionDetails>
        <Box>
          {workScheduleSections.map((section) => (
            <Accordion
              key={section.id}
                expanded={expandedSubSection === section.id}
                onChange={handleSubSectionChange(section.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    {section.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{section.title}</Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ 
                    '& h1': { fontSize: '1.5rem', fontWeight: 'bold', mb: 2, color: 'primary.main' },
                    '& h2': { fontSize: '1.25rem', fontWeight: 'bold', mb: 1.5, mt: 2, color: 'primary.dark' },
                    '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mb: 1, mt: 1.5 },
                    '& p': { mb: 1.5, lineHeight: 1.6 },
                    '& ul': { mb: 1.5, pl: 2 },
                    '& ol': { mb: 1.5, pl: 2 },
                    '& li': { mb: 0.5 },
                    '& strong': { fontWeight: 'bold', color: 'primary.main' },
                    '& em': { fontStyle: 'italic' },
                    '& code': { 
                      backgroundColor: 'rgba(0,0,0,0.1)', 
                      padding: '2px 4px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace'
                    }
                  }}>
                    <Typography 
                      component="div" 
                      dangerouslySetInnerHTML={{ 
                        __html: section.content.replace(/\n/g, '<br/>') 
                      }} 
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Main Accordion: Sandėliukas */}
      <Accordion
        expanded={expandedMainSection === 'storage'}
        onChange={handleMainSectionChange('storage')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <StorageIcon />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5">Sandėliukas</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Spustelėkite, kad išplėstumėte ir sužinotumėte daugiau
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
        <AccordionDetails>
          <Box>
            {storageSections.map((section) => (
              <Accordion
                key={section.id}
                expanded={expandedSubSection === section.id}
                onChange={handleSubSectionChange(section.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      {section.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{section.title}</Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  '& h1': { fontSize: '1.5rem', fontWeight: 'bold', mb: 2, color: 'primary.main' },
                  '& h2': { fontSize: '1.25rem', fontWeight: 'bold', mb: 1.5, mt: 2, color: 'primary.dark' },
                  '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mb: 1, mt: 1.5 },
                  '& p': { mb: 1.5, lineHeight: 1.6 },
                  '& ul': { mb: 1.5, pl: 2 },
                  '& ol': { mb: 1.5, pl: 2 },
                  '& li': { mb: 0.5 },
                  '& strong': { fontWeight: 'bold', color: 'primary.main' },
                  '& em': { fontStyle: 'italic' },
                  '& code': { 
                    backgroundColor: 'rgba(0,0,0,0.1)', 
                    padding: '2px 4px', 
                    borderRadius: '3px',
                    fontFamily: 'monospace'
                  }
                }}>
                  <Typography 
                    component="div" 
                    dangerouslySetInnerHTML={{ 
                      __html: section.content.replace(/\n/g, '<br/>') 
                    }} 
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        </AccordionDetails>
      </Accordion>

      {/* Main Accordion: Pardavimai */}
      <Accordion
        expanded={expandedMainSection === 'pardavimai'}
        onChange={handleMainSectionChange('pardavimai')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <ShoppingCartIcon />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5">Pardavimai</Typography>
              <Typography variant="body2" color="text.secondary">
                Spustelėkite, kad išplėstumėte ir sužinotumėte daugiau
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {pardavimaiSections.map((section) => (
              <Accordion
                key={section.id}
                expanded={expandedSubSection === section.id}
                onChange={handleSubSectionChange(section.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      {section.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{section.title}</Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ 
                    '& h1': { fontSize: '1.5rem', fontWeight: 'bold', mb: 2, color: 'primary.main' },
                    '& h2': { fontSize: '1.25rem', fontWeight: 'bold', mb: 1.5, mt: 2, color: 'primary.dark' },
                    '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mb: 1, mt: 1.5 },
                    '& p': { mb: 1.5, lineHeight: 1.6 },
                    '& ul': { mb: 1.5, pl: 2 },
                    '& ol': { mb: 1.5, pl: 2 },
                    '& li': { mb: 0.5 },
                    '& strong': { fontWeight: 'bold', color: 'primary.main' },
                    '& em': { fontStyle: 'italic' },
                    '& code': { 
                      backgroundColor: 'rgba(0,0,0,0.1)', 
                      padding: '2px 4px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace'
                    }
                  }}>
                    <Typography 
                      component="div" 
                      dangerouslySetInnerHTML={{ 
                        __html: section.content.replace(/\n/g, '<br/>') 
                      }} 
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Footer */}
      <Card sx={{ mt: 3, bgcolor: 'grey.100' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Reikia Pagalbos?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Jei neradote atsakymo į savo klausimą arba susidūrėte su problema, kreipkitės į administratorius per sistemos pranešimus arba tiesiogiai.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dokumentacija;